import { OUTPUT_PURPOSE, SURFACE } from 'Pods/ThreeDStudio/Store/constants'

import { EMPTY_OBJECT } from 'Utils/stableDefaults'

import { buildCanonicalParams } from './buildCanonicalParams'
import { getStoreValueForParam } from './getStoreValueForParam'

/**
 * Builds the POST /api/internal/generator/runs payload for an image run, the way
 * buildGeneratePayload does for a 3D run. The composer settings resolve through the
 * imageSettings store mapping (aspect_ratio, num_images); what the composer owns outside
 * the store - the prompt, the reference assets, the overlay flag - is handed in keyed by
 * canonical name and wins over the mapping. Whatever the schema does not declare is
 * dropped: output_purpose, for one, only reaches the actions whose catalog entry has a
 * 3D-input prompt template. It is general unless the caller says the image feeds 3D
 * generation, stated rather than left to the backend's own default.
 * The action rides on use_case, which is what the backend reads to rank the
 * providers offering it. Locking the run to the schema's provider is the caller's call:
 * pass model_id through additionalPayloadData.
 *
 * @param {Object} schema - An IMAGE_SOURCES entry
 * @param {Object} values - Canonical values, e.g. { prompt, input_image, output_purpose, overlay_mask }
 * @param {Object|null} settings - The imageSettings slice the run is sent with, as
 *   captured when the turn was sent; null when the composer shows no settings controls
 * @param {Object} [additionalPayloadData] - Extra top-level keys (model_id, private, generator_session_id)
 */
export const buildImageGeneratePayload = (schema, values, settings, additionalPayloadData = undefined) => {
  if (!schema) {
    throw new Error('No image schema found')
  }

  const state = { imageSettings: settings || EMPTY_OBJECT }
  const resolvedValues = { ...values, output_purpose: values?.output_purpose ?? OUTPUT_PURPOSE.GENERAL }
  const canonicalParams = buildCanonicalParams(schema, (key) => {
    const value = resolvedValues[key]

    return value !== undefined ? value : getStoreValueForParam(key, 'imageSettings', state)
  })

  return {
    data: {
      run_type: 'image_generation',
      use_case: schema.action,
      // The Render tab submits image_generation runs from the 3D Studio; run_type alone
      // cannot identify the surface, so the client declares it explicitly.
      surface: SURFACE.IMAGE_GENERATOR,
      ...additionalPayloadData,
      canonical_params: canonicalParams,
    },
  }
}
